import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { Popup } from '../../../components/Popup'
import { Select } from '../../../components/Select'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { getCCForAssign, getHospitalCCForAssign, sendCCForAssign } from './api'
import { formatOptionNameValue } from '../../../helper/optionsFormatters'
import { setLoading } from '../../../redux/store/common/slice'
import { BTN_TXT, LABELS, PLACEHOLDERS } from '../../../constants'
import { TOption } from '../../../components/Select/types'
import { AddCCForm, AddCCPopupProps } from './types'
import { addCCToHospitalUser } from '../api'

export const AddCCPopup = ({
  isOpen,
  setIsOpen,
  onUpdateCCList,
  userId,
  hospitalId
}: AddCCPopupProps) => {
  const [contractCategories, setContractCategories] = useState<TOption[]>([])
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const watchSelectedCC = Form.useWatch('contract_category', form)

  useEffect(() => {
    if (isOpen) {
      onGetCCList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onGetCCList = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const { data } = hospitalId
        ? await getHospitalCCForAssign(userId, hospitalId)
        : await getCCForAssign(userId)
      setContractCategories(
        !!data.results.length ? data.results.map(formatOptionNameValue) : []
      )
    } finally {
      dispatch(setLoading(false))
    }
  }, [hospitalId, dispatch, userId])

  const onCancel = () => {
    form.resetFields()
    setIsOpen(false)
  }

  const handleFinishForm = async ({ contract_category }: AddCCForm) => {
    try {
      dispatch(setLoading(true))
      if (hospitalId) {
        await addCCToHospitalUser({
          hospitalId,
          userId,
          data: {
            contract_category
          }
        })
      } else {
        await sendCCForAssign(userId, contract_category)
      }
      await onUpdateCCList()
      onCancel()
    } catch (e) {
      dispatch(setLoading(false))
    }
  }

  return (
    <Popup
      visible={isOpen}
      onCancel={onCancel}
      width={447}
      title="Assign contract category"
      className="add-contract-category-popup"
      footer={
        <>
          <Button upperCase onClick={onCancel}>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            disabled={!watchSelectedCC}
            form="add-cc-form-id"
            htmlType="submit"
            upperCase
          >
            {BTN_TXT.ADD}
          </Button>
        </>
      }
    >
      <CustomForm<AddCCForm>
        form={form}
        onFinish={handleFinishForm}
        id="add-cc-form-id"
      >
        <Select
          options={contractCategories}
          propsItem={{
            label: LABELS.CONTRACT_CATEGORY,
            name: 'contract_category',
            required: true
          }}
          propsSelect={{
            getPopupContainer: (node) => document?.body ?? node,
            placeholder: PLACEHOLDERS.PLEASE_SELECT
          }}
        />
      </CustomForm>
    </Popup>
  )
}
